import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAuthMessage } from '../utils/authErrors';
import { LogIn, Mail, Lock, AlertCircle } from 'lucide-react';

type LocationState = { from?: { pathname: string; search?: string; hash?: string } };

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from;
  const redirectTo = from ? `${from.pathname}${from.search ?? ''}${from.hash ?? ''}` : '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      await login(email, password);
      navigate(redirectTo, { replace: true });
    } catch (err: unknown) {
      setError(getAuthMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      await loginWithGoogle();
      // loginWithGoogle triggers an OAuth redirect; navigation happens automatically after redirect
    } catch (err: unknown) {
      setError(getAuthMessage(err));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-tech-dark text-tech-text flex items-center justify-center px-4 bg-tech-mesh">
      <div className="max-w-md w-full">
        <div className="glass-panel rounded-2xl p-8 sm:p-10 border border-tech-cyan/15 shadow-app-lg ring-1 ring-white/[0.04]">
          <div className="flex justify-center mb-6">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
            >
              <LogIn className="w-6 h-6 text-white" aria-hidden />
            </div>
          </div>
          <h2 className="font-['Space_Grotesk'] text-2xl font-bold text-center mb-2 text-tech-text">Welcome Back</h2>
          <p className="text-tech-text-muted text-center mb-8">
            Sign in to your account
            {from && (
              <span className="block mt-2 text-sm text-tech-cyan/90">
                Continue where you left off after signing in.
              </span>
            )}
          </p>

          {error && (
            <div className="mb-4 p-3 bg-amber-500/20 border border-amber-500/50 rounded-lg flex items-center gap-2 text-amber-200" role="alert">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="login-email" className="block text-tech-text-muted mb-2 font-medium">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-tech-text-muted" aria-hidden />
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-tech-gray border border-tech-border text-tech-text placeholder-tech-text-muted/50 focus:outline-none focus:ring-2 focus:ring-tech-cyan/50 focus:border-tech-cyan"
                  placeholder="Enter your email"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="login-password" className="block text-tech-text-muted mb-2 font-medium">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-tech-text-muted" aria-hidden />
                <input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-tech-gray border border-tech-border text-tech-text placeholder-tech-text-muted/50 focus:outline-none focus:ring-2 focus:ring-tech-cyan/50 focus:border-tech-cyan"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl font-semibold text-tech-dark hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-tech-cyan/50"
              style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-tech-border" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-tech-gray text-tech-text-muted">Or continue with</span>
              </div>
            </div>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="mt-4 w-full py-3 bg-tech-gray border border-tech-border rounded-xl font-medium text-tech-text hover:bg-tech-gray-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 focus:outline-none focus:ring-2 focus:ring-tech-cyan/50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              <span>Continue with Google</span>
            </button>
          </div>

          <div className="mt-6 text-center space-y-2">
            <Link to="/forgot-password" className="text-tech-cyan hover:text-tech-cyan-dark text-sm">
              Forgot password?
            </Link>
            <p className="text-tech-text-muted text-sm">
              Don&apos;t have an account?{' '}
              <Link to="/signup" state={location.state} className="text-tech-cyan hover:text-tech-cyan-dark font-semibold">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
